import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Alert, AlertTitle, Link, Skeleton } from "@mui/material";
import * as React from "react";

import { useNavigation } from "@/providers";
import {
  DatasetsList,
  type DatasetRow,
} from "@/shared/components/datasets/DatasetsList";
import { EventsList } from "@/shared/components/events";
import { FilesList } from "@/shared/components/files";
import { SelectionOption } from "@/shared/components/ResourceView";
import { type CollectionResourceType } from "@/shared/domain/collections";
import { useUpdateCollection } from "@/shared/domain/collections/hooks/useUpdateCollection";
import {
  type SearchTablePreferenceKey,
  useSearchTablePreferences,
} from "@/shared/domain/config/hooks";
import { EventRecord, type EventQueryScope } from "@/shared/domain/events";
import { FileRecord, type FilesQueryScope } from "@/shared/domain/files";
import { Condition } from "@/types";

import styles from "./CollectionItemsTab.module.css";
import { useCollectionOutletContext } from "./useCollectionOutletContext";

// Returns `undefined` for a `resource_type` this build doesn't render yet
// (e.g. a collection created via the SDK/CLI ahead of the matching frontend
// release). This switch is the single source of truth for which types the
// component supports; `CollectionItemsTab` treats `undefined` as its signal
// to short-circuit to a generic "unsupported" message.
function resourceTypeToPreferenceKey(
  resourceType: CollectionResourceType,
): SearchTablePreferenceKey | undefined {
  switch (resourceType) {
    case "dataset":
      return "dataset_search_table_preferences";
    case "event":
      return "event_search_table_preferences";
    case "file":
      return "file_search_table_preferences";
    default:
      return undefined;
  }
}

export const CollectionItemsTab: React.FC = () => {
  const collection = useCollectionOutletContext();

  const preferenceKey = resourceTypeToPreferenceKey(collection.resource_type);
  const { isLoading: preferencesLoading } = useSearchTablePreferences(
    // Hooks can't be called conditionally: fall back to a harmless key for
    // an unsupported resource_type, since its result goes unused once the
    // component short-circuits below.
    preferenceKey ?? "file_search_table_preferences",
  );

  const { goto } = useNavigation();

  const { mutate: updateCollection, error } = useUpdateCollection();

  const collectionConditions: Condition[] = React.useMemo(
    () => [
      {
        field: "collection.collection_id",
        comparator: "EQUALS" as const,
        value: collection.collection_id,
      },
    ],
    [collection.collection_id],
  );

  const eventScope: EventQueryScope = React.useMemo(
    () => ({ type: "collection", id: collection.collection_id }),
    [collection.collection_id],
  );

  const fileScope: FilesQueryScope = React.useMemo(
    () => ({ type: "collection", id: collection.collection_id }),
    [collection.collection_id],
  );

  const datasetSelectionOptions: SelectionOption<DatasetRow>[] = React.useMemo(
    () => [
      {
        name: "Remove from Collection",
        icon: <RemoveCircleOutlineIcon fontSize="small" />,
        action: ({ selected }: { selected: DatasetRow[] }) => {
          updateCollection({
            collectionId: collection.collection_id,
            resourceOwnerId: collection.org_id,
            updates: {
              remove_resources: selected.map((d) => ({
                resource_type: "dataset" as const,
                resource_id: d.dataset_id,
              })),
            },
          });
        },
      },
    ],
    [collection.collection_id, collection.org_id, updateCollection],
  );

  const eventSelectionOptions: SelectionOption<EventRecord>[] = React.useMemo(
    () => [
      {
        name: "Remove from Collection",
        icon: <RemoveCircleOutlineIcon fontSize="small" />,
        action: ({ selected }: { selected: EventRecord[] }) => {
          updateCollection({
            collectionId: collection.collection_id,
            resourceOwnerId: collection.org_id,
            updates: {
              remove_resources: selected.map((e: EventRecord) => ({
                resource_type: "event" as const,
                resource_id: e.event_id,
              })),
            },
          });
        },
      },
    ],
    [collection.collection_id, collection.org_id, updateCollection],
  );

  const fileSelectionOptions: SelectionOption<FileRecord>[] = React.useMemo(
    () => [
      {
        name: "Remove from Collection",
        icon: <RemoveCircleOutlineIcon fontSize="small" />,
        action: ({ selected }) => {
          updateCollection({
            collectionId: collection.collection_id,
            resourceOwnerId: collection.org_id,
            updates: {
              remove_resources: selected.map((f) => ({
                resource_type: "file" as const,
                resource_id: f.file_id,
              })),
            },
          });
        },
      },
      {
        name: "Visualize",
        icon: <VisibilityIcon fontSize="small" />,
        action: ({ selected }) => {
          goto.visualize({
            dataSource: { files: selected.map((f) => f.file_id) },
          });
        },
      },
    ],
    [collection.collection_id, collection.org_id, updateCollection, goto],
  );

  if (preferencesLoading) {
    return (
      <Skeleton
        variant="rectangular"
        width="100%"
        height={300}
        style={{ marginTop: "var(--spacing-sm)" }}
      />
    );
  }

  if (preferenceKey === undefined) {
    return (
      <Alert severity="info">
        <AlertTitle>Unsupported collection type</AlertTitle>
        This collection holds a resource type this version of the app
        doesn&apos;t support yet. Reload once the app updates, or use the CLI or
        SDK to view its contents.
      </Alert>
    );
  }

  const isEmpty =
    (collection.resources[collection.resource_type]?.length ?? 0) === 0;

  if (isEmpty) {
    return (
      <Alert severity="info">
        <AlertTitle>Empty Collection</AlertTitle>
        {collection.resource_type === "dataset" && (
          <>
            There are no items in this collection yet. You can add{" "}
            <Link href="/datasets" underline="hover">
              datasets
            </Link>{" "}
            to it.
          </>
        )}
        {collection.resource_type === "file" && (
          <>
            There are no items in this collection yet. You can add individual
            files to it.
          </>
        )}
        {collection.resource_type === "event" && (
          <>
            There are no items in this collection yet. You can add individual
            events to it.
          </>
        )}
      </Alert>
    );
  }

  return (
    <>
      {error && (
        <Alert severity="error" style={{ marginBottom: "var(--spacing-xs)" }}>
          {error.message}
        </Alert>
      )}

      {collection.resource_type === "dataset" && (
        <DatasetsList
          conditions={collectionConditions}
          selectionOptions={datasetSelectionOptions}
        />
      )}

      {collection.resource_type === "file" && (
        <FilesList scope={fileScope} selectionOptions={fileSelectionOptions} />
      )}

      {collection.resource_type === "event" && (
        <div className={styles.eventsTableWrapper}>
          <EventsList
            scope={eventScope}
            selectionOptions={eventSelectionOptions}
          />
        </div>
      )}
    </>
  );
};
